import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'

type Language = 'tr' | 'en' | 'ru'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

// Translation imports
import translations from '../locales/translations'

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to get language from localStorage
    const saved = localStorage.getItem('language') as Language
    if (saved === 'tr' || saved === 'en' || saved === 'ru') {
      return saved
    }
    // Default to Turkish
    return 'tr'
  })

  useEffect(() => {
    localStorage.setItem('language', language)
    document.documentElement.lang = language
  }, [language])

  // Generate canonical and alternate URLs
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const currentPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''
  const canonicalUrl = `${baseUrl}${currentPath}`
  const alternateUrls = {
    'tr': `${baseUrl}${currentPath}`,
    'en': `${baseUrl}${currentPath}`,
    'ru': `${baseUrl}${currentPath}`,
  }

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
  }

  const t = (key: string): string => {
    const keys = key.split('.')
    let value: any = translations[language]
    
    for (const k of keys) {
      value = value?.[k]
    }
    
    return value || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <Helmet>
        <html lang={language} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hreflang="tr" href={alternateUrls.tr} />
        <link rel="alternate" hreflang="en" href={alternateUrls.en} />
        <link rel="alternate" hreflang="ru" href={alternateUrls.ru} />
        <link rel="alternate" hreflang="x-default" href={alternateUrls.tr} />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : language === 'en' ? 'en_US' : 'ru_RU'} />
        <meta property="og:locale:alternate" content={language === 'tr' ? 'en_US' : 'tr_TR'} />
      </Helmet>
      {children}
    </LanguageContext.Provider>
  )
}














